import { useEffect, useLayoutEffect, useRef } from "react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { useLanguage } from "@/contexts/LanguageContext";
import { cn } from "@/lib/utils";
import type { ChatMessage } from "@/contexts/SimpleChatContext";

interface MessageStreamProps {
    messages: ChatMessage[];
    onRetry: (originalText: string, messageId: string) => void;
}

const linkRegex = /\[([^\]]+)\]\(([^)]+)\)/g;

const parseMarkdownLinks = (text: string) => {
    const parts: (string | JSX.Element)[] = [];
    let lastIndex = 0;
    let match: RegExpExecArray | null;
    let keyIndex = 0;

    while ((match = linkRegex.exec(text)) !== null) {
        if (match.index > lastIndex) {
            parts.push(text.slice(lastIndex, match.index));
        }
        parts.push(
            <a
                key={keyIndex++}
                href={match[2]}
                target="_blank"
                rel="noopener noreferrer"
                className="font-bold underline underline-offset-[3px] decoration-1 transition-colors"
                style={{
                    backgroundImage: "var(--gradient-accent-v2)",
                    WebkitBackgroundClip: "text",
                    backgroundClip: "text",
                    color: "transparent",
                    textDecorationColor: "rgba(255,255,255,0.4)",
                }}
            >
                {match[1]}
            </a>,
        );
        lastIndex = match.index + match[0].length;
    }
    if (lastIndex < text.length) parts.push(text.slice(lastIndex));
    return parts.length > 0 ? parts : text;
};

const TypingDots = () => (
    <div className="flex items-center gap-1.5 py-0.5">
        {[0, 1, 2].map((i) => (
            <motion.span
                key={i}
                className="w-1.5 h-1.5"
                style={{ background: "var(--gradient-accent-v2)" }}
                animate={{ opacity: [0.25, 1, 0.25] }}
                transition={{
                    duration: 0.9,
                    repeat: Infinity,
                    delay: i * 0.18,
                    ease: "easeInOut",
                }}
            />
        ))}
    </div>
);

/**
 * Renders the conversation feed for HeroChat. User bubbles right-align, bot
 * bubbles left-align (mirrored automatically by the parent's `dir`). Streaming
 * shows typing dots while empty and a blinking cursor once chunks arrive.
 *
 * Auto-scroll follows new content only when the user is already near the
 * bottom — so reading older messages mid-stream isn't yanked.
 */
const MessageStream = ({ messages, onRetry }: MessageStreamProps) => {
    const { t } = useTranslation();
    const { dir } = useLanguage();
    const scrollRef = useRef<HTMLDivElement>(null);
    const stickToBottomRef = useRef(true);

    useLayoutEffect(() => {
        const el = scrollRef.current;
        if (el) el.scrollTop = el.scrollHeight;
    }, []);

    useEffect(() => {
        const el = scrollRef.current;
        if (!el || !stickToBottomRef.current) return;
        el.scrollTop = el.scrollHeight;
    }, [messages]);

    const handleScroll = () => {
        const el = scrollRef.current;
        if (!el) return;
        stickToBottomRef.current =
            el.scrollHeight - el.scrollTop - el.clientHeight < 80;
    };

    return (
        <div
            ref={scrollRef}
            onScroll={handleScroll}
            data-lenis-prevent
            dir={dir}
            className="flex-1 min-h-0 overflow-y-auto overscroll-contain px-3 sm:px-4 py-4 custom-scrollbar"
        >
            <div className="flex flex-col justify-end min-h-full gap-3">
                {messages.map((message) => {
                    const isUser = message.role === "user";
                    const isStreaming = !isUser && message.streaming === true;
                    const isEmptyStreaming =
                        isStreaming && message.content.length === 0;
                    const hasError = !!message.error;
                    return (
                        <div
                            key={message.id}
                            className={cn(
                                "flex flex-col gap-1",
                                isUser ? "items-end" : "items-start",
                            )}
                        >
                            <div
                                dir="auto"
                                className={cn(
                                    "relative max-w-[85%] px-4 py-3 text-sm leading-relaxed whitespace-pre-wrap border",
                                    isUser
                                        ? "bg-white/[0.04] border-white/20 text-white"
                                        : "bg-white/[0.02] border-white/10 text-white",
                                )}
                            >
                                {!isUser && (
                                    <span
                                        aria-hidden
                                        className="absolute top-0 ltr:left-0 rtl:right-0 w-8 h-px"
                                        style={{
                                            background: "var(--gradient-accent-v2)",
                                        }}
                                    />
                                )}
                                {isUser && (
                                    <span
                                        aria-hidden
                                        className="absolute -top-1.5 ltr:right-2 rtl:left-2 font-mono-brutal text-[8px] text-white/40 bg-background px-1.5"
                                    >
                                        YOU
                                    </span>
                                )}
                                {isEmptyStreaming ? (
                                    <TypingDots />
                                ) : hasError && !message.content ? (
                                    <span className="text-white/55 text-sm">
                                        {t("hero_chat.generic_error")}
                                    </span>
                                ) : (
                                    <>
                                        {parseMarkdownLinks(message.content)}
                                        {isStreaming && (
                                            <motion.span
                                                aria-hidden
                                                className="inline-block w-[2px] h-[1em] align-[-2px] ms-1"
                                                style={{
                                                    background:
                                                        "var(--gradient-accent-v2)",
                                                }}
                                                animate={{ opacity: [1, 0.2, 1] }}
                                                transition={{
                                                    duration: 1,
                                                    repeat: Infinity,
                                                    ease: "easeInOut",
                                                }}
                                            />
                                        )}
                                    </>
                                )}
                            </div>
                            {hasError && message.error && (
                                <button
                                    type="button"
                                    onClick={() =>
                                        onRetry(
                                            message.error!.originalText,
                                            message.id,
                                        )
                                    }
                                    className="font-mono-brutal text-[10px] text-white/55 hover:text-white underline underline-offset-2 transition-colors"
                                >
                                    {t("hero_chat.try_again")}
                                </button>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default MessageStream;
